import { NgClass, NgForOf, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { UserRoleEnum } from '@enums/user-role.enum';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { animate, style, transition, trigger } from '@angular/animations';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RoleManagerHelper } from '@helpers/role-manager.helper';
import { CustomerSidebarHttpService } from '@services/customer-sidebar-http.service';
import { LocalStorageService } from '@services/local-storage.service';
import { isNil } from '@utils/util';
import { ILoggedInUserViewModel } from 'src/app/model/common.model';
import { ProfileSharedService } from '../../../../pages/v2/profile/services/profile-shared.service';
import { EventConstant } from '../../../../sse/event.constant';
import { SignalrService } from '../../../../sse/signalr.service';
import { HeaderComponent } from '../../../header/components/header/header.component';
import { MenuItemInterface } from '../../interfaces/menu-item.interface';
import { ServiceRequestTypeDynamicNumberInterface } from '../../interfaces/service-request-type-dynamic-number.interface';
import { SidebarService } from '../../services/sidebar.service';
import { SidebarActionsComponent } from '../sidebar-actions/sidebar-actions.component';
import { SidebarHeaderComponent } from '../sidebar-header/sidebar-header.component';
import { SidebarMenuItemComponent } from '../sidebar-menu-item/sidebar-menu-item.component';
import { SidebarSwitchRoleComponent } from '../sidebar-switch-role/sidebar-switch-role.component';
import { SidebarUserInfoComponent } from '../sidebar-user-info/sidebar-user-info.component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  // todo: ChangeDetectionStrategy.OnPush needs to be used, but currently we have a problem with loading spinners that doesn't disappear
  // changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTooltipModule,
    MatIconModule,
    NgIf,
    NgForOf,
    NgClass,
    MatSidenavModule,
    RouterOutlet,
    MatButtonModule,
    BrowserAnimationsModule,
    HeaderComponent,
    SidebarUserInfoComponent,
    SidebarHeaderComponent,
    SidebarMenuItemComponent,
    SidebarActionsComponent,
    SidebarSwitchRoleComponent
  ],
  providers: [SidebarService],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ 
          position: 'absolute', 
          width: '100%', 
          transform: 'translateX({{startPos}}%)',
          opacity: 0 
        }),
        animate('300ms ease-out', style({ transform: 'translateX(0%)', opacity: 1 }))
      ], { params: { startPos: 100 } }),
      
      transition(':leave', [
        style({ position: 'absolute', width: '100%' }),
        animate('300ms ease-out', style({ 
          transform: 'translateX({{endPos}}%)', 
          opacity: 0 
        }))
      ], { params: { endPos: -100 } })
    ])
  ]
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Output() signOutClicked: EventEmitter<void> = new EventEmitter();

  /**
   * @default true for mobiles, false for other
   */
  isMenuOpen = window.innerWidth > 482;

  menuItems: MenuItemInterface[] = [];
  currentUser: ILoggedInUserViewModel = this.localStorageService.getCurrentUserDetails();
  isCustomerRole = this.roleManagerHelper.isCustomerRole();
  isCustomerVendorRole = this.roleManagerHelper.isCustomerVendorRole();
  private subscriptions: Subscription[] = [];
  serviceRequestDynamicData: ServiceRequestTypeDynamicNumberInterface;
  serviceRequestQueue: number | null = null;
  showOverlay = false;
  showRoleList = false;

  constructor(
    private readonly router: Router,
    private readonly sidebarService: SidebarService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly localStorageService: LocalStorageService,
    private readonly signalRService: SignalrService,
    private readonly roleManagerHelper: RoleManagerHelper,
    private readonly customerSidebarHttpService: CustomerSidebarHttpService,
    private readonly profileSharedService: ProfileSharedService,
  ) {}

  ngOnInit() {
    this.setMenuItems();
    this.subscribeOnCurrentUserProfileUpdates();

    if (this.roleManagerHelper.isCustomerRole()) {
      this.subscribeOnCustomersRequestChangeEvent();
      this.getNumberOfCustomerServiceRequests();
    }

    if (this.currentUser.role === UserRoleEnum.COORDINATOR || this.currentUser.role === UserRoleEnum.MANAGEMENT) {
      this.getNumberOfCustomerPendingServiceRequests();
      this.subscribeOnCustomersPendingRequestsChangeEvent();
    }

    // Listen on route change only on mobile device
    if (window.innerWidth < 482) {
      this.subscribeOnRouteChange();
    }
  }


  /**
   * TrackBy function for *ngFor directive to optimize rendering.
   * Returns a unique id for each menu item
   *
   * @param _index - ngFor item index
   * @param item - menu item
   */
  trackById(_index: number, item: MenuItemInterface): string {
    return item.id;
  }

  /**
   * get number of customer service requests
   */
  private getNumberOfCustomerServiceRequests(): void {
    const getNumberOfCustomerServiceRequestsSub = this.customerSidebarHttpService.getNumberOfCustomerServiceRequests()
      .subscribe((response: ServiceRequestTypeDynamicNumberInterface) => {
        if (response) {
          this.serviceRequestDynamicData = response;
        }
      });
    this.subscriptions.push(getNumberOfCustomerServiceRequestsSub);
  }

  /**
   * get number of customer pending service requests
   */
  private getNumberOfCustomerPendingServiceRequests(): void {
    const getNumberOfCustomerServiceRequestsSub = this.customerSidebarHttpService.getNumberOfCustomerPendingServiceRequests()
      .subscribe((response: number) => {
        if (!isNil(response)) {
          this.serviceRequestQueue = response;
        }
      });
    this.subscriptions.push(getNumberOfCustomerServiceRequestsSub);
  }

  /**
   * subscribe on Signal R `CustomersRequestChangeEvent` and save data once emitted
   */
  private subscribeOnCustomersRequestChangeEvent(): void {
    this.signalRService.sseEventListener(EventConstant.customersRequestChangeEvent);
    const eventDataChangeSub = this.signalRService.eventDataChange.subscribe(value => {
      if (value.eventName === EventConstant.customersRequestChangeEvent) {
        this.serviceRequestDynamicData = value.data;
      }
    });
    this.subscriptions.push(eventDataChangeSub);
  }

  /**
   * subscribe on Signal R `CustomersPendingRequestsChangeEvent` and save data once emitted
   */
  private subscribeOnCustomersPendingRequestsChangeEvent(): void {
    this.signalRService.sseEventListener(EventConstant.customersPendingRequestsChangeEvent);
    const eventDataChangeSub = this.signalRService.eventDataChange.subscribe(value => {
      if (value.eventName === EventConstant.customersPendingRequestsChangeEvent) {
        this.serviceRequestQueue = value.data;
      }
    });
    this.subscriptions.push(eventDataChangeSub);
  }

  /**
   * subscribe on route change to close sidebar on mobile devices
   */
  private subscribeOnRouteChange(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd && this.isMenuOpen)
    ).subscribe((): void => {
      this.isMenuOpen = false;
    });
  }

  /**
   * set menu items based on current user role
   */
  private setMenuItems(): void {
    const userRole = this.localStorageService.getCurrentUserRole() as UserRoleEnum;

    this.menuItems = this.sidebarService.getMenuItemsByRole(userRole);

    this.changeDetectorRef.detectChanges();
  }

  subscribeOnCurrentUserProfileUpdates(): void {
    const currentUserProfileUpdatesSub = this.profileSharedService.profileUpdates$
      .subscribe({
        next: (status: boolean) => {
          if (status) {
            this.currentUser = this.localStorageService.getCurrentUserDetails();
            this.changeDetectorRef.markForCheck();
          }
        },
      });
    this.subscriptions.push(currentUserProfileUpdatesSub);
  }

  checkOpenMenu(value: boolean): void {
    this.showOverlay = value;
  }

  openRoleList(value: boolean): void {
    this.showRoleList = value;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
